import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import AddInCircleIcon from '@grnhse/seedling/lib/birch/icons/add_in_circle';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import SingleFilter from 'talent_matching/components/filters/single_filter';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

type Props = {
  fieldName: string;
  title: string;
  getButtonLabel: (count: number) => string;
  addLabel: string;
  clearLabel: string;
  typeOptions: Item[];
  width?: string;
  disabled?: boolean;
  disabledTooltip?: string | null;
};

const FilterPopover = ({
  fieldName,
  title,
  getButtonLabel,
  addLabel,
  clearLabel,
  typeOptions,
  width = '700px',
  disabled = false,
  disabledTooltip,
}: Props) => {
  const { setValue, watch } = useFormContext();
  const filters = (watch(fieldName) as AdvancedFilter[]) || [];

  const handleAddFilter = () => {
    setValue(fieldName, [...filters, new AdvancedFilter()], {
      shouldDirty: true,
    });
  };

  const clear = () => {
    setValue(fieldName, [], { shouldDirty: true });
  };

  const handleOnChange = (index: number, filter: AdvancedFilter) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1, filter);
    setValue(fieldName, newFilters, { shouldDirty: true });
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    setValue(fieldName, newFilters, { shouldDirty: true });
  };

  const totalApplied = filters.filter((f: AdvancedFilter) =>
    f.applied()
  ).length;
  const buttonLabel = getButtonLabel(totalApplied);

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={title}
      width={width}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
    >
      <Stack spacing={2} direction="column" sx={{ width: '100%' }}>
        {filters.map((filter: AdvancedFilter, index: number) => (
          <SingleFilter
            key={filter.reactKey}
            filter={filter}
            onChange={(updatedFilter: AdvancedFilter) =>
              handleOnChange(index, updatedFilter)
            }
            onRemove={() => handleRemoveFilter(index)}
            typeOptions={typeOptions}
          />
        ))}

        <Stack spacing={1} direction="column" alignItems="flex-start">
          <SecondaryButton
            onClick={handleAddFilter}
            icon={AddInCircleIcon}
            size="md"
          >
            {addLabel}
          </SecondaryButton>
          {filters.length > 0 && (
            <TertiaryButton onClick={clear}>{clearLabel}</TertiaryButton>
          )}
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default FilterPopover;
